import { useState } from "react";
import { Star, StarHalf } from "lucide-react";
import { cn } from "../lib/utils";

/**
 * Half-star rating component.
 * Allowed values: 0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5
 */
export function HalfStarRating({ value = 0, onChange, size = "md", readOnly = false, className }) {
  const [hoverValue, setHoverValue] = useState(0);
  const displayValue = hoverValue || value;
  const sizes = { sm: "w-4 h-4", md: "w-5 h-5", lg: "w-6 h-6" };
  const iconSize = sizes[size] || sizes.md;

  const handleClick = (starIndex, isLeftHalf) => {
    if (readOnly) return;
    const newValue = isLeftHalf ? starIndex - 0.5 : starIndex;
    onChange?.(newValue === value ? 0 : newValue);
  };

  const handleMouseMove = (starIndex, e) => {
    if (readOnly) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const isLeftHalf = e.clientX - rect.left < rect.width / 2;
    setHoverValue(isLeftHalf ? starIndex - 0.5 : starIndex);
  };

  return (
    <div
      className={cn("flex items-center gap-0.5", readOnly ? "pointer-events-none" : "cursor-pointer", className)}
      onMouseLeave={() => setHoverValue(0)}
      data-testid="half-star-rating"
    >
      {[1, 2, 3, 4, 5].map((starIndex) => {
        const filled = displayValue >= starIndex;
        const halfFilled = !filled && displayValue >= starIndex - 0.5;

        return (
          <span
            key={starIndex}
            className="relative inline-flex"
            onMouseMove={(e) => handleMouseMove(starIndex, e)}
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const isLeftHalf = e.clientX - rect.left < rect.width / 2;
              handleClick(starIndex, isLeftHalf);
            }}
          >
            {filled ? (
              <Star className={cn(iconSize, "fill-yellow-400 text-yellow-400 transition-colors")} />
            ) : halfFilled ? (
              <span className="relative">
                <Star className={cn(iconSize, "text-border")} />
                <span className="absolute inset-0 overflow-hidden w-1/2">
                  <Star className={cn(iconSize, "fill-yellow-400 text-yellow-400")} />
                </span>
              </span>
            ) : (
              <Star className={cn(iconSize, "text-border hover:text-yellow-300 transition-colors")} />
            )}
          </span>
        );
      })}
      {value > 0 && (
        <span className="ml-1.5 text-xs font-medium text-muted-foreground tabular-nums">{value.toFixed(1)}</span>
      )}
    </div>
  );
}
